import Alert from '@pelckmans/elementis/components/alert';
import BlockHeader from '@pelckmans/elementis/components/blockheader';
import Card from '@pelckmans/elementis/components/card';
import Skeleton from '@pelckmans/elementis/components/skeleton';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModuleStatus from '../../../components/ModuleStatus';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../constants/analyticsEvents';
import IdentityContext from '../../../contexts/IdentityContext';
import useAnalytics from '../../../hooks/useAnalytics';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useRoutes } from '../../../hooks/useRoutes';
import { Method, Subject } from '../api/methodsApi';
import useMethods from '../hooks/useMethods';
import useModules from '../hooks/useModules';
import useSubjects from '../hooks/useSubjects';
import { hasAccess } from '../utils';
import MethodStatus from './MethodStatus';

const BASE = 'PAGES.CATALOGUE';

function SubjectSkeletonLoader() {
  const { t } = useTranslation();

  return (
    <Skeleton animate className="skeleton-subjects" aria-label={t(`${BASE}.LOADING_STATES.SUBJECT`)}>
      {Object.keys(Array.from({ length: 12 })).map(i => (
        <Skeleton.Bone height={56} key={i} />
      ))}
    </Skeleton>
  );
}

function CardSkeletonLoader({ 'aria-label': ariaLabel }: { 'aria-label': string }) {
  return (
    <Skeleton animate className="skeleton-cards" aria-label={ariaLabel}>
      {Object.keys(Array.from({ length: 6 })).map(i => (
        <Skeleton.Bone height={234} key={i} />
      ))}
    </Skeleton>
  );
}

type Props = {
  demoModules: string[];
};

function Catalogue({ demoModules }: Props) {
  const { identity } = useContext(IdentityContext);
  const { t } = useTranslation();
  const { buildAbsolutePath } = useRoutes();
  const analytics = useAnalytics();

  const [selectedSubject, setSelectedSubject] = useState<Subject | undefined>(undefined);
  const [selectedMethod, setSelectedMethod] = useState<Method | undefined>(undefined);

  const { subjects, loading: isSubjectLoading } = useSubjects();
  const { methods, loading: isMethodsLoading, resetMethods } = useMethods(selectedSubject?.id);
  const { modules, loading: isModulesLoading, resetModules } = useModules(selectedMethod?.id);

  const methodRef = useRef<HTMLDivElement>(null);
  const moduleRef = useRef<HTMLDivElement>(null);

  const isLoading = isMethodsLoading || isSubjectLoading || isModulesLoading;

  useDocumentTitle(t(`${BASE}.DOCUMENT_TITLE`));

  useEffect(() => {
    if (subjects.length === 1) setSelectedSubject(subjects[0]);
  }, [selectedSubject, subjects]);

  const getMethodAlert = () => {
    if (isLoading) return undefined;

    if (!selectedSubject) {
      return <Alert prominence="info">{t(`${BASE}.METHOD.ALERTS.NO_SUBJECT.TITLE`)}</Alert>;
    }

    if (methods && methods.length === 0) {
      return <Alert prominence="warning">{t(`${BASE}.METHOD.ALERTS.NO_METHOD.TITLE`)}</Alert>;
    }
    return undefined;
  };

  const getModuleAlert = () => {
    if (isLoading) return undefined;

    if (!selectedSubject && !selectedMethod) {
      return <Alert prominence="info">{t(`${BASE}.MODULE.ALERTS.NO_METHOD_NO_SUBJECT.TITLE`)}</Alert>;
    }
    if (selectedSubject && !selectedMethod) {
      return <Alert prominence="info">{t(`${BASE}.MODULE.ALERTS.NO_METHOD.TITLE`)}</Alert>;
    }
    if (modules.length === 0 && selectedMethod) {
      return <Alert prominence="warning">{t(`${BASE}.MODULE.ALERTS.NO_MODULE.TITLE`)}</Alert>;
    }
    return undefined;
  };

  useEffect(() => {
    if (!isMethodsLoading && methods) {
      setTimeout(() => {
        methodRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [isMethodsLoading, methods]);

  useEffect(() => {
    if (!isModulesLoading) {
      setTimeout(() => {
        moduleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [isModulesLoading]);

  const logEvent = (id: string) => {
    analytics.capture({
      eventType: ANALYTICS_EVENT_TYPES.MODULE_ACCESSED,
      objectId: id,
      subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_CATALOG,
    });
  };

  return (
    <>
      <div className="catalogue-subjects">
        <BlockHeader title={t(`${BASE}.SUBJECT.TITLE`)} titleTag="h2" />
        {isSubjectLoading && <SubjectSkeletonLoader />}
        <div className="subjects-container">
          {subjects.map(subject => (
            <div key={subject.id}>
              <Card
                titleText={subject.name}
                titleTag="h3"
                as="radio"
                onClick={() => {
                  if (selectedSubject?.id === subject.id) return;
                  setSelectedSubject(subject);
                  setSelectedMethod(undefined);
                  resetMethods();
                  resetModules();
                }}
                selected={selectedSubject?.id === subject.id}
                disabled={subjects.length === 1}
                name={subject.name}
              />
            </div>
          ))}
        </div>
      </div>
      <div ref={methodRef} data-testid="methods" className="catalogue-methods">
        <BlockHeader
          title={t(`${BASE}.METHOD.${selectedSubject ? 'SELECTED_TITLE' : 'TITLE'}`, {
            subject: selectedSubject?.name,
          })}
          titleTag="h2"
          alert={getMethodAlert()}
        />
        {isMethodsLoading && selectedSubject && <CardSkeletonLoader aria-label={t(`${BASE}.LOADING_STATES.METHOD`)} />}
        {methods.length > 0 && (
          <div className="cards-container">
            {methods.map(method => (
              <Card
                key={method.id}
                titleText={method.catalogName}
                titleTag="h3"
                image={method.logo || 'placeholder'}
                selected={selectedMethod?.id === method.id}
                status={{
                  children: <MethodStatus method={method} role={identity.highestRole} />,
                  'aria-label': undefined,
                  role: undefined,
                }}
                {...(method.externalContentUrl
                  ? {
                      external: true,
                      externalAriaLabel: t('PAGES.CATALOGUE.METHOD.EXTERNAL.A11Y_LABEL', {
                        method: method.catalogName,
                      }),
                      target: '_blank',
                      rel: 'noopener noreferrer',
                      href: method.externalContentUrl!,
                      as: 'link',
                    }
                  : {
                      onClick: () => {
                        setSelectedMethod(method);
                      },
                      as: 'radio',
                      name: method.catalogName,
                    })}
              />
            ))}
          </div>
        )}
      </div>
      <div ref={moduleRef} className="catalogue-modules" data-testid="modules">
        <BlockHeader
          title={t(`${BASE}.MODULE.${selectedMethod ? 'SELECTED_TITLE' : 'TITLE'}`, {
            method: selectedMethod?.name,
          })}
          titleTag="h2"
          alert={getModuleAlert()}
        />
        {isModulesLoading && selectedMethod && <CardSkeletonLoader aria-label={t(`${BASE}.LOADING_STATES.MODULE`)} />}
        {!isModulesLoading && modules.length > 0 && (
          <div className="cards-container">
            {modules.map(module => {
              const userHasAccess = hasAccess(identity, module.publishingState, module.id);
              return (
                <Card
                  status={{
                    children: <ModuleStatus access={userHasAccess} module={module} demoModules={demoModules} />,
                    'aria-label': t('PAGES.CATALOGUE.MODULE.STATUS_GROUP.A11Y_LABEL'),
                    role: 'group',
                  }}
                  disabled={!userHasAccess}
                  key={module.id}
                  titleText={module.catalogName}
                  titleTag="h3"
                  image={module.styling?.logo || 'placeholder'}
                  as="link"
                  onClick={() => logEvent(module.id)}
                  {...(module.externalContentUrl
                    ? {
                        external: true,
                        externalAriaLabel: t('PAGES.CATALOGUE.MODULE.EXTERNAL.A11Y_LABEL', {
                          module: module.catalogName,
                        }),
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        href: module.externalContentUrl!,
                      }
                    : {
                        href: buildAbsolutePath(['MATERIAL'], {
                          subjectSlug: selectedSubject!.slug,
                          methodSlug: selectedMethod?.slug!,
                          moduleSlug: module.slug,
                        }),
                      })}
                />
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

export default Catalogue;
